/**
 * Onboarding flow — 3-step wizard (RC-P2, STA-127).
 *
 * Step 1 — Welcome / app intro
 * Step 2 — Nickname (required, 1-64 chars)
 * Step 3 — Referral code (optional; pre-filled from PendingReferral)
 *
 * On step-3 commit (or skip) we PUT /me/profile with
 * `{ nickname, onboarding_complete: true }`, then navigate to /reading.
 * Referral redemption (if a code was entered) happens BEFORE the profile
 * commit so a malformed-code error doesn't strand the user with
 * onboarding_complete=false.
 *
 * State machine is internal (useState) — no router sub-routes — so the
 * back button never lands the user on a half-filled form.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, PendingReferral, api, type ReferralSource } from '../api';
import { useAuth } from '../auth';

type Step = 1 | 2 | 3;

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [nickname, setNickname] = useState<string>(user?.nickname || '');
  const [refCode, setRefCode] = useState<string>('');
  const [refSource, setRefSource] = useState<ReferralSource>('onboarding');
  const [refLocked, setRefLocked] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill referral code from a pre-login URL `?ref=` capture.
  useEffect(() => {
    const pending = PendingReferral.get();
    if (pending) {
      setRefCode(pending.code);
      setRefSource(pending.source);
      setRefLocked(true);
    }
  }, []);

  // If user has somehow already completed onboarding, bounce them out.
  useEffect(() => {
    if (user?.onboarding_complete) {
      navigate('/reading', { replace: true });
    }
  }, [user, navigate]);

  if (!user) return null;

  const goNext = () => setStep((s) => (s < 3 ? ((s + 1) as Step) : s));
  const goBack = () => setStep((s) => (s > 1 ? ((s - 1) as Step) : s));

  const submitNicknameStep = () => {
    const trimmed = nickname.trim();
    if (!trimmed) {
      setError('暱稱不可為空');
      return;
    }
    if (trimmed.length > 64) {
      setError('暱稱長度不可超過 64 字');
      return;
    }
    setError(null);
    goNext();
  };

  const finishOnboarding = async (opts: { withRedeem: boolean }) => {
    setError(null);
    setSubmitting(true);
    try {
      // 1) Optional: redeem referral code (errors here are recoverable —
      // user can correct or skip without losing nickname).
      if (opts.withRedeem) {
        const code = refCode.trim().toUpperCase();
        try {
          await api.redeemReferral(code, refSource);
        } catch (e) {
          if (e instanceof ApiError) {
            const detail = e.detail as
              | { error?: { code?: string; message?: string } }
              | undefined;
            const msg = detail?.error?.message || `兌換失敗（${e.status}）`;
            setError(msg);
            setSubmitting(false);
            return;
          }
          setError('兌換時出現問題，請稍後再試。');
          setSubmitting(false);
          return;
        }
        // Successful redeem — clear the pending payload so it's not re-applied.
        PendingReferral.clear();
      }

      // 2) Commit profile + flip onboarding_complete.
      await api.updateProfile({
        nickname: nickname.trim(),
        onboarding_complete: true,
      });
      await refresh();
      navigate('/reading', { replace: true });
    } catch (e) {
      setError(
        e instanceof ApiError
          ? `儲存失敗（${e.status}）`
          : '儲存失敗，請稍後再試。',
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <div className="flex items-center gap-2 mb-8" aria-label="進度">
        {[1, 2, 3].map((n) => (
          <div
            key={n}
            className={`h-1 flex-1 rounded-full ${
              n <= step ? 'bg-tarot-gold' : 'bg-tarot-purple/20'
            }`}
          />
        ))}
      </div>

      {step === 1 && (
        <section className="text-center space-y-6">
          <h1 className="text-3xl text-tarot-gold">歡迎來到 Soul-Tarot</h1>
          <p className="text-tarot-muted leading-relaxed">
            每天一次塔羅，由 AI 牌靈為你解讀。
            <br />
            連續登入有獎，邀請朋友雙方都拿 Stardust。
          </p>
          <div className="card text-left text-sm space-y-2">
            <div>★ 每日 1 次免費抽牌</div>
            <div>★ 解讀完可付費追問同位牌靈</div>
            <div>★ 連續簽到、被邀請都可累積 Stardust</div>
          </div>
          <button onClick={goNext} className="btn-primary w-full">
            開始
          </button>
        </section>
      )}

      {step === 2 && (
        <section className="space-y-4">
          <h2 className="text-2xl text-tarot-gold">怎麼稱呼你？</h2>
          <p className="text-tarot-muted text-sm">
            這個名字會出現在你的帳號與朋友的通知裡。之後可以再改。
          </p>
          <input
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            maxLength={64}
            placeholder="輸入暱稱"
            className="w-full px-4 py-3 rounded-md bg-tarot-purple/10 border border-tarot-purple/30 text-tarot-text focus:outline-none focus:border-tarot-gold"
            autoFocus
          />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3">
            <button onClick={goBack} className="btn-ghost flex-1">
              上一步
            </button>
            <button onClick={submitNicknameStep} className="btn-primary flex-1">
              下一步
            </button>
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="space-y-4">
          <h2 className="text-2xl text-tarot-gold">有人推薦你來嗎？</h2>
          <p className="text-tarot-muted text-sm">
            輸入 8 碼大寫推薦碼，雙方在你連續簽到 7 天後各獲得 50★。
            {refLocked && (
              <span className="block mt-1 text-xs text-tarot-gold">
                已從你點擊的連結帶入推薦碼，確認後送出即可。
              </span>
            )}
          </p>
          <input
            value={refCode}
            onChange={(e) => setRefCode(e.target.value.toUpperCase())}
            maxLength={16}
            placeholder="例：ABCDEFGH"
            className="w-full px-4 py-3 rounded-md bg-tarot-purple/10 border border-tarot-purple/30 text-tarot-text focus:outline-none focus:border-tarot-gold uppercase tracking-widest"
          />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3">
            <button onClick={goBack} className="btn-ghost flex-1" disabled={submitting}>
              上一步
            </button>
            <button
              onClick={() => finishOnboarding({ withRedeem: false })}
              className="btn-ghost flex-1"
              disabled={submitting}
            >
              略過
            </button>
            <button
              onClick={() => finishOnboarding({ withRedeem: refCode.trim().length > 0 })}
              className="btn-primary flex-1"
              disabled={submitting}
            >
              {submitting ? '送出中…' : '完成'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
